import { activeTool } from '../interaction/tool_service.js?v=20260904f';
import { selectConnectedSketchWalls } from './sketch_wall_selection_system.js';
import { SKETCH_WALL_TEMPLATES, buildSketchWallTemplate, transformSketchWallTemplates } from './sketch_wall_templates.mjs';
import { constrainDrawingPoint, pointOnDrawingRay } from '../interaction/drawing_angle_snap.mjs';
import { S } from '../app/app_state.js?v=20260817c';
import { AppRuntime } from '../app/app_runtime.js?v=20260706d';
import { getPlanSvg, screenToWorld, setStatus } from '../render/plan_renderer.js?v=20260904f';
import { recordUndo } from '../services/history_service.js?v=20260903c';
import { markProjectModelChanged } from '../project/project_revision_service.js';
import * as SelectionService from '../selection/selection_service.js?v=20260904f';
import { clearObstacleSelection } from '../obstacles/obstacles.js?v=20260903c';
import { solveSketchWalls, wrapAngle } from './sketch_wall_solver.js?v=20260706a';
import {
  SKETCH_WALL_SYSTEM_ID,
  ensureSketchWallState,
  sketchExactAngleRadians,
  allSketchNodes,
  allSketchWalls,
  allSketchAngles,
  allSketchStraightJoints,
  sketchNodeById,
  sketchWallsAtNode,
  sketchWallById,
  sketchWallOtherNodeId,
  sketchWallSideLength,
  sketchWallAbsoluteAngle,
  sketchWallConnectedNodeIds,
  sketchDirectedAngleValueAt,
  sketchAngleDisplayDegrees,
  sketchAnglePairedSiblingId,
  createSketchNode,
  createSketchWall,
  deleteSketchWall,
  deleteSketchNodeIfOrphan,
  mergeSketchNodes,
  findNearestSketchNode,
  findNearestSketchWallPoint,
  splitSketchWall,
  applySketchTJunctionStemAllowanceAtNode,
  updateSketchWallLength,
  updateSketchAngleValueDegrees,
  toggleSketchWallFixed,
  setSketchWallAxis,
  clearSketchWallSelection,
  selectSketchWallEntity,
  sketchWallDrawDefaults,
  createSketchWallOpening,
  updateSketchWallOpening,
  deleteSketchWallOpening,
  SKETCH_WALL_MIN_LENGTH,
  SKETCH_WALL_JOIN_TOLERANCE
} from './sketch_wall_state.js?v=20260904f';

function selectSketchWallEntityExclusive(patch) {
  SelectionService.clearSystemSelectionsExcept(SKETCH_WALL_SYSTEM_ID);
  clearObstacleSelection();
  selectSketchWallEntity(patch);
}

function requestSketchWallPlanRender(reason) {
  AppRuntime.requestPlanRender(reason);
}

function requestSketchWallAppRefresh(reason) {
  if (/-drop$/.test(String(reason || ''))) markProjectModelChanged(reason);
  AppRuntime.requestAppRefresh(false, reason);
}

function isTypingTarget(target) {
  const tag = target?.tagName?.toLowerCase();
  return tag === 'input' || tag === 'select' || tag === 'textarea' || target?.isContentEditable;
}

function sketchWallDrawToolActive() {
  ensureSketchWallState();
  return S.ui.sketchWallTool === 'draw';
}

function syncSketchWallToolbarButton() {
  const button = document.getElementById('btnSketchWall');
  const active = sketchWallDrawToolActive();
  button?.classList.toggle('is-active', active);
  button?.setAttribute('aria-pressed', active ? 'true' : 'false');
}

let sketchWallTemplatePlacement = null;
const invalidSketchWallTemplateLengths = new Set();

// Clears the in-progress draw chain's start point, discarding the node it
// was pinned to if that node never actually ended up anchoring a wall (see
// deleteSketchNodeIfOrphan) — otherwise cancelling mid-draw (Escape, or
// switching straight to a different tool) leaves an orphaned, undeletable
// point sitting on the canvas forever, since nothing else ever references
// it and the normal wall/node delete paths have no reason to look for it.
function abandonSketchWallDraw() {
  sketchWallTemplatePlacement = null;
  deleteSketchNodeIfOrphan(S.ui.sketchWallDraw.startNodeId);
  S.ui.sketchWallDraw.startNodeId = null;
  S.ui.sketchWallDraw.currentPoint = null;
}

const sketchWallTemplateSettings = { matrix: [-1, 0, 0, -1], lengths: { L: [...SKETCH_WALL_TEMPLATES.L], U: [...SKETCH_WALL_TEMPLATES.U] } };

function getSketchWallTemplateSettings() { return sketchWallTemplateSettings; }
function updateSketchWallTemplateSettings(kind, index, value) {
  if (kind === 'rotate' || kind === 'mirror') {
    sketchWallTemplateSettings.matrix = transformSketchWallTemplates(sketchWallTemplateSettings.matrix, kind);
  } else if (SKETCH_WALL_TEMPLATES[kind] && Number.isInteger(index) && index >= 0 && index < SKETCH_WALL_TEMPLATES[kind].length) {
    const key = `${kind}:${index}`;
    if (!Number.isFinite(value) || value < SKETCH_WALL_MIN_LENGTH) {
      invalidSketchWallTemplateLengths.add(key);
      requestSketchWallPlanRender('sketch-wall-template-invalid');
      return false;
    }
    invalidSketchWallTemplateLengths.delete(key);
    sketchWallTemplateSettings.lengths[kind][index] = value;
  } else return false;
  requestSketchWallPlanRender('sketch-wall-template-settings');
  return true;
}
function getSketchWallTemplatePreview() {
  const placement = sketchWallTemplatePlacement;
  if (!placement) return null;
  if (placement.project !== S.project || placement.sheetId !== S.drawingSheets?.activeSheetId
    || placement.view !== S.project?.currentView || !sketchWallDrawToolActive()) {
    sketchWallTemplatePlacement = null;
    if (S.ui.sketchWallTool === 'draw') { S.ui.sketchWallTool = null; syncSketchWallToolbarButton(); }
    return null;
  }
  if ([...invalidSketchWallTemplateLengths].some(key => key.startsWith(`${placement.kind}:`))) return null;
  return buildSketchWallTemplate(placement.kind, sketchWallTemplateSettings.lengths[placement.kind],
    sketchWallDrawDefaults(), sketchWallTemplateSettings.matrix, placement.point);
}
function armSketchWallTemplate(kind) {
  if (!SKETCH_WALL_TEMPLATES[kind]) return false;
  if ([...invalidSketchWallTemplateLengths].some(key => key.startsWith(`${kind}:`))) return false;
  activateSketchWallDrawTool();
  clearSketchWallSelection();
  S.ui.sketchWallActiveSidebarCard = 'templates';
  sketchWallTemplatePlacement = { kind, project: S.project, sheetId: S.drawingSheets?.activeSheetId,
    view: S.project?.currentView, point: { x: S.camera2D?.x || 0, z: S.camera2D?.z || 0 } };
  setStatus(`${kind}-Vorlage · mit Linksklick platzieren · Esc: abbrechen`);
  requestSketchWallAppRefresh('sketch-wall-template-arm');
  return true;
}
function placeSketchWallTemplate(event) {
  const preview = getSketchWallTemplatePreview();
  if (!preview) return true; // A stale placement must not start a new drawing chain.
  sketchWallTemplatePlacement.point = worldFromMouseEvent(event);
  const graph = getSketchWallTemplatePreview();
  recordUndo('Wandvorlage einfügen');
  const nodes = new Map(graph.nodes.map((n, i) => [n.id, createSketchNode(n.x, n.z, i === 1)]));
  const walls = graph.walls.map(w => createSketchWall(nodes.get(w.startNodeId).id, nodes.get(w.endNodeId).id, w));
  const ids = new Set(walls.map(w => w.id));
  for (const angle of allSketchAngles()) {
    if (!ids.has(angle.wallAId) || !ids.has(angle.wallBId)) continue;
    if (sketchDirectedAngleValueAt(angle.nodeId, angle.wallAId, angle.wallBId) > Math.PI) {
      [angle.wallAId, angle.wallBId] = [angle.wallBId, angle.wallAId];
    }
    angle.value = Math.PI / 2;
  }
  walls.forEach((wall, index) => updateSketchWallLength(wall.id, graph.lengths[index], 'inner'));
  // Anchor location and heading; entered inner dimensions are authored constraints.
  walls[0].fixed = true; walls[0].fixedSource = 'chain';
  walls[0].fixedAngle = sketchExactAngleRadians(sketchWallAbsoluteAngle(walls[0]));
  solveSketchWalls();
  sketchWallTemplatePlacement = null;
  deactivateSketchWallDrawTool();
  selectSketchWallEntityExclusive({ wallId: walls[0].id });
  setStatus('Wandvorlage eingefügt · Innenlängen und Wände sind normal bearbeitbar');
  requestSketchWallAppRefresh('sketch-wall-template-drop');
  return true;
}

function activateSketchWallDrawTool() {
  ensureSketchWallState();
  // Right sidebar can only show one panel at a time (see AGENTS.md) —
  // Ebenen owns its own auto-close for selection-driven panels, but this
  // tool's flag lives outside S.selection, so it must close Ebenen itself.
  AppRuntime.closeLevelsPanelForToolChange();
  S.ui.sketchWallTool = 'draw';
  clearSketchWallSelection();
  abandonSketchWallDraw();
  setStatus('Wand zeichnen · Startpunkt setzen');
  syncSketchWallToolbarButton();
  setSketchWallDrawCursor(null);
  requestSketchWallAppRefresh('sketch-wall-tool-activate');
}

function deactivateSketchWallDrawTool() {
  ensureSketchWallState();
  S.ui.sketchWallTool = null;
  abandonSketchWallDraw();
  syncSketchWallToolbarButton();
  clearSketchWallDrawCursor();
  requestSketchWallAppRefresh('sketch-wall-tool-deactivate');
}

function toggleSketchWallDrawTool() {
  if (sketchWallDrawToolActive()) deactivateSketchWallDrawTool();
  else activateSketchWallDrawTool();
}

// Türe/Fenster share the same S.ui.sketchWallTool mutex as the draw tool
// (only one of 'draw' | 'door' | 'window' | null at a time) — they differ
// only in which preset dimensions get applied on placement (see
// createSketchWallOpening's SKETCH_WALL_OPENING_PRESETS), never in behavior.
function sketchWallOpeningToolActive() {
  ensureSketchWallState();
  return S.ui.sketchWallTool === 'door' || S.ui.sketchWallTool === 'window';
}

function activateSketchWallOpeningTool(kind) {
  ensureSketchWallState();
  AppRuntime.closeLevelsPanelForToolChange();
  const safeKind = kind === 'window' ? 'window' : 'door';
  S.ui.sketchWallTool = safeKind;
  abandonSketchWallDraw();
  clearSketchWallDrawCursor();
  setStatus(safeKind === 'window' ? 'Fenster · auf eine Wand klicken' : 'Türe · auf eine Wand klicken');
  requestSketchWallAppRefresh('sketch-wall-opening-tool-activate');
}

function deactivateSketchWallOpeningTool() {
  ensureSketchWallState();
  S.ui.sketchWallTool = null;
  requestSketchWallAppRefresh('sketch-wall-opening-tool-deactivate');
}

function toggleSketchWallOpeningTool(kind) {
  const safeKind = kind === 'window' ? 'window' : 'door';
  if (S.ui.sketchWallTool === safeKind) deactivateSketchWallOpeningTool();
  else activateSketchWallOpeningTool(safeKind);
}

// "Schnitt" shares the same S.ui.sketchWallTool mutex too, but — unlike
// Türe/Fenster, which place onto whichever wall gets clicked — it's scoped
// to one specific wall (S.ui.sketchWallSplitWallId, the one selected when
// the button was pressed): the split only executes if the next click
// actually lands on THAT wall's own body, so the exact cut position can be
// chosen (e.g. cleanly between two Ausschnitte) instead of always landing
// on the midpoint.
function sketchWallSplitToolActive() {
  ensureSketchWallState();
  return S.ui.sketchWallTool === 'split';
}

function activateSketchWallSplitTool(wallId) {
  ensureSketchWallState();
  AppRuntime.closeLevelsPanelForToolChange();
  S.ui.sketchWallTool = 'split';
  S.ui.sketchWallSplitWallId = wallId;
  abandonSketchWallDraw();
  clearSketchWallDrawCursor();
  setStatus('Schnitt · Position auf der Wand anklicken');
  requestSketchWallAppRefresh('sketch-wall-split-tool-activate');
}

function deactivateSketchWallSplitTool() {
  ensureSketchWallState();
  S.ui.sketchWallTool = null;
  S.ui.sketchWallSplitWallId = null;
  requestSketchWallAppRefresh('sketch-wall-split-tool-deactivate');
}

function toggleSketchWallSplitTool(wallId) {
  if (sketchWallSplitToolActive() && S.ui.sketchWallSplitWallId === wallId) {
    deactivateSketchWallSplitTool();
  } else {
    activateSketchWallSplitTool(wallId);
  }
}

// "Parallel" shares the same S.ui.sketchWallTool mutex and the same
// "scoped to one wall, act on the next click" shape as "Schnitt" above
// (S.ui.sketchWallParallelWallId instead of ...SplitWallId): the wall
// selected when the button was pressed is the one that gets rotated; the
// next wall the user clicks is only ever used as the angle REFERENCE, never
// touched itself. See handleGlobalSketchWallParallelClick for the actual
// one-time rotation.
function sketchWallParallelToolActive() {
  ensureSketchWallState();
  return S.ui.sketchWallTool === 'parallel';
}

function activateSketchWallParallelTool(wallId) {
  ensureSketchWallState();
  AppRuntime.closeLevelsPanelForToolChange();
  S.ui.sketchWallTool = 'parallel';
  S.ui.sketchWallParallelWallId = wallId;
  abandonSketchWallDraw();
  clearSketchWallDrawCursor();
  setStatus('Parallel · Referenzwand anklicken');
  requestSketchWallAppRefresh('sketch-wall-parallel-tool-activate');
}

function deactivateSketchWallParallelTool() {
  ensureSketchWallState();
  S.ui.sketchWallTool = null;
  S.ui.sketchWallParallelWallId = null;
  requestSketchWallAppRefresh('sketch-wall-parallel-tool-deactivate');
}

function toggleSketchWallParallelTool(wallId) {
  if (sketchWallParallelToolActive() && S.ui.sketchWallParallelWallId === wallId) {
    deactivateSketchWallParallelTool();
  } else {
    activateSketchWallParallelTool(wallId);
  }
}

function worldFromMouseEvent(event) {
  const svg = getPlanSvg();
  const rect = svg.getBoundingClientRect();
  return screenToWorld(event.clientX - rect.left, event.clientY - rect.top);
}

const SKETCH_WALL_SNAP_SCREEN_PX = 16;

// A fixed-mm snap radius is a moving target on screen: zoomed out, it can
// shrink to a fraction of a pixel and make snapping onto an existing node
// (or closing a loop back onto the chain's own start) practically
// impossible to hit. Keep a minimum comfortable screen-pixel hit radius
// regardless of zoom.
function sketchWallSnapTolerance() {
  const scale = S.camera2D?.scale || 1;
  return Math.max(SKETCH_WALL_JOIN_TOLERANCE, SKETCH_WALL_SNAP_SCREEN_PX / scale);
}

const SKETCH_WALL_CURSOR_CLASSES = ['sketch-draw-active', 'sketch-draw-cursor-t', 'sketch-draw-cursor-l'];

// 'node' → hovering close enough to an existing node to merge into it
// (Eckverbindung, 'L' cursor); 'wall' → close enough to an existing wall's
// body to split it (T-Verbindung, 'T' cursor); null → free point. Shared by
// the draw-preview/drag cursor feedback and (indirectly, by construction)
// the click/drop snap logic itself, so the cursor always promises exactly
// what committing right now would do. `excludeNodeId` keeps a point/wall
// already anchored to the node currently being placed/dragged from
// trivially "snapping to itself".
function sketchWallHoverConnectionKind(point, tolerance, excludeNodeId = null) {
  let bestNode = null;
  let bestNodeDist = tolerance;
  for (const candidate of allSketchNodes()) {
    if (candidate.id === excludeNodeId) continue;
    const dist = Math.hypot(candidate.x - point.x, candidate.z - point.z);
    if (dist <= bestNodeDist) {
      bestNodeDist = dist;
      bestNode = candidate;
    }
  }
  if (bestNode) return 'node';
  const wallHit = findNearestSketchWallPoint(point, tolerance);
  if (wallHit && wallHit.wall.startNodeId !== excludeNodeId && wallHit.wall.endNodeId !== excludeNodeId) return 'wall';
  return null;
}

function setSketchWallDrawCursor(kind) {
  const svg = getPlanSvg();
  if (!svg) return;
  svg.classList.remove(...SKETCH_WALL_CURSOR_CLASSES);
  svg.classList.add(kind === 'wall' ? 'sketch-draw-cursor-t' : kind === 'node' ? 'sketch-draw-cursor-l' : 'sketch-draw-active');
}

function clearSketchWallDrawCursor() {
  const svg = getPlanSvg();
  svg?.classList.remove(...SKETCH_WALL_CURSOR_CLASSES);
}

// After dragging one or more nodes, check each against every node *not*
// in the dragged set — if one lands within snap tolerance, merge them into
// a single shared node. This is what turns "dragged a loose wall's
// endpoint next to another wall's endpoint" into an actual joined corner
// instead of two coincident-but-unconnected points sitting on top of each
// other. Failing that, check whether it landed on an existing wall's BODY
// instead — same T-Verbindung a fresh draw already gets when its point
// lands mid-wall (see handleGlobalSketchWallDrawClick): split that wall at
// the dropped point and merge the dragged node into the new joint, rather
// than leaving it sitting there disconnected.
function attemptSketchWallSnapMerge(draggedNodeIds) {
  const tolerance = sketchWallSnapTolerance();
  const candidates = allSketchNodes().filter(node => !draggedNodeIds.has(node.id));
  for (const draggedId of draggedNodeIds) {
    const dragged = sketchNodeById(draggedId);
    if (!dragged) continue;
    let best = null;
    let bestDist = tolerance;
    for (const candidate of candidates) {
      const dist = Math.hypot(candidate.x - dragged.x, candidate.z - dragged.z);
      if (dist <= bestDist) {
        bestDist = dist;
        best = candidate;
      }
    }
    if (best) {
      mergeSketchNodes(draggedId, best.id);
      applySketchTJunctionStemAllowanceAtNode(best.id);
      continue;
    }
    const wallHit = findNearestSketchWallPoint(dragged, tolerance);
    if (wallHit && wallHit.wall.startNodeId !== draggedId && wallHit.wall.endNodeId !== draggedId) {
      const split = splitSketchWall(wallHit.wall.id, wallHit.point);
      if (split) {
        mergeSketchNodes(draggedId, split.midNode.id);
        applySketchTJunctionStemAllowanceAtNode(split.midNode.id);
      }
    }
  }
}

// "Click anywhere on this wall" hit test for placing an Ausschnitt — unlike
// findNearestSketchWallPoint (a tight snap radius for precision joining),
// this accepts any point within the wall's own visible body (its half-
// thickness plus a small click margin), and only within the wall's own
// span (no snapping past its ends onto a neighbor).
const SKETCH_WALL_OPENING_CLICK_MARGIN = 30; // mm, beyond the wall's own face

function findSketchWallBodyHit(point) {
  let best = null;
  for (const wall of allSketchWalls()) {
    const start = sketchNodeById(wall.startNodeId);
    const end = sketchNodeById(wall.endNodeId);
    if (!start || !end) continue;
    const dx = end.x - start.x;
    const dz = end.z - start.z;
    const lenSq = dx * dx + dz * dz;
    if (lenSq < 1) continue;
    const length = Math.sqrt(lenSq);
    const t = ((point.x - start.x) * dx + (point.z - start.z) * dz) / lenSq;
    if (t < 0 || t > 1) continue;
    const projX = start.x + dx * t;
    const projZ = start.z + dz * t;
    const dist = Math.hypot(point.x - projX, point.z - projZ);
    const tolerance = (Number(wall.thickness) || 0) / 2 + SKETCH_WALL_OPENING_CLICK_MARGIN;
    if (dist <= tolerance && (!best || dist < best.dist)) {
      best = { wall, offset: t * length, dist };
    }
  }
  return best;
}

function handleGlobalSketchWallOpeningClick(event) {
  if (!sketchWallOpeningToolActive()) return false;
  event.preventDefault();
  event.stopPropagation();
  const point = worldFromMouseEvent(event);
  const hit = findSketchWallBodyHit(point);
  if (!hit) {
    setStatus('Keine Wand an dieser Position gefunden');
    return true;
  }
  const kind = S.ui.sketchWallTool === 'window' ? 'window' : 'door';
  recordUndo(kind === 'window' ? 'Fenster einfügen' : 'Türe einfügen');
  const opening = createSketchWallOpening(hit.wall.id, hit.offset, kind);
  // Select the new opening itself, not just its wall — otherwise the
  // Ausschnitte sidebar card (which the user was just placing from) would
  // immediately snap shut in favor of Parametrische Wände.
  selectSketchWallEntityExclusive({ wallId: hit.wall.id, openingId: opening?.id || null });
  // Deactivate right after placing one — unlike the draw tool (which stays
  // armed for a whole chain of walls), staying armed here made it too easy
  // to accidentally drop a second Ausschnitt with the next stray click.
  deactivateSketchWallOpeningTool();
  setStatus(kind === 'window' ? 'Fenster eingefügt' : 'Türe eingefügt');
  requestSketchWallAppRefresh('sketch-wall-opening-insert');
  return true;
}

// Executes the armed "Schnitt": only a click that actually lands on the
// wall the tool was armed for counts — clicking anywhere else just repeats
// the hint so the user can try again (mirrors the Ausschnitt tool's own
// "wrong spot" handling) rather than silently cancelling. splitSketchWall
// itself now carries the wall's Ausschnitte/Vormauerung across the cut
// (see there), so this doesn't need to do anything extra for that.
function handleGlobalSketchWallSplitClick(event) {
  if (!sketchWallSplitToolActive()) return false;
  event.preventDefault();
  event.stopPropagation();
  const wallId = S.ui.sketchWallSplitWallId;
  const wall = wallId ? sketchWallById(wallId) : null;
  if (!wall) {
    deactivateSketchWallSplitTool();
    return true;
  }
  const point = worldFromMouseEvent(event);
  const hit = findSketchWallBodyHit(point);
  if (!hit || hit.wall.id !== wallId) {
    setStatus('Bitte auf die markierte Wand klicken');
    return true;
  }
  recordUndo('Wand schneiden');
  const result = splitSketchWall(wallId, point);
  deactivateSketchWallSplitTool();
  if (!result) {
    setStatus('Wand ist zu kurz zum Schneiden');
    requestSketchWallAppRefresh('sketch-wall-split-failed');
    return true;
  }
  solveSketchWalls();
  selectSketchWallEntityExclusive({ wallId: result.wallA.id });
  setStatus('Wand geschnitten · 180° fest definiert');
  requestSketchWallAppRefresh('sketch-wall-split');
  return true;
}

// Rotates the wall the "Parallel" button was pressed for to match whichever
// OTHER wall the user clicks next — same step-wise, constraint-respecting
// rotation approach as alignSelectedSketchWall (see there), but driven by an
// arbitrary reference wall's own angle instead of a fixed horizontal/
// vertical candidate, and picking whichever of that reference's two facing
// directions needs the smaller turn (parallel only means the same LINE, a
// wall drawn in the opposite direction is just as "parallel").
//
// Crucially this is a ONE-TIME nudge, not a standing constraint: unlike
// Horizontal/Vertikal there is no persistent axis flag to convert the
// temporary fixed/fixedAngle drive into afterward, so — unless the wall was
// already independently fixed before this ran — that temporary pin is
// released again once the target angle is reached. The rotation itself
// stays (nothing pulls the geometry back once the solve has already settled
// there); only the constraint that drove it there goes away.
function handleGlobalSketchWallParallelClick(event) {
  if (!sketchWallParallelToolActive()) return false;
  event.preventDefault();
  event.stopPropagation();
  const wallId = S.ui.sketchWallParallelWallId;
  const wall = wallId ? sketchWallById(wallId) : null;
  if (!wall) {
    deactivateSketchWallParallelTool();
    return true;
  }
  const point = worldFromMouseEvent(event);
  const hit = findSketchWallBodyHit(point);
  if (!hit || hit.wall.id === wallId) {
    setStatus('Bitte eine andere Wand als Referenz anklicken');
    return true;
  }
  const reference = hit.wall;
  deactivateSketchWallParallelTool();

  const current = sketchWallAbsoluteAngle(wall);
  const referenceAngle = sketchWallAbsoluteAngle(reference);
  const candidates = [referenceAngle, referenceAngle + Math.PI];
  let target = candidates[0];
  let bestDelta = Infinity;
  for (const candidate of candidates) {
    const delta = Math.abs(wrapAngle(candidate - current));
    if (delta < bestDelta) {
      bestDelta = delta;
      target = current + wrapAngle(candidate - current);
    }
  }

  recordUndo('Wand parallel ausrichten');
  const wasFixed = wall.fixed;
  const previousFixedSource = wall.fixedSource;
  if (!wasFixed) wall.fixed = true;
  if (!wasFixed) wall.fixedSource = 'axis';
  const setAngle = v => { wall.fixedAngle = v; };
  const result = stepSolveTowardValue(setAngle, current, target);
  if (!wasFixed) {
    wall.fixed = false;
    wall.fixedAngle = null;
    wall.fixedSource = previousFixedSource || null;
  }
  solveSketchWalls();
  if (!result.ok) {
    setStatus('Widerspruch: diese Wand kann nicht parallel ausgerichtet werden — zuerst eine widersprüchliche Bemaßung aufheben');
    requestSketchWallAppRefresh('sketch-wall-parallel-conflict');
    return true;
  }
  setStatus('Wand parallel ausgerichtet');
  requestSketchWallAppRefresh('sketch-wall-parallel');
  return true;
}

function handleGlobalSketchWallDrawClick(event) {
  if (!sketchWallDrawToolActive() || event.button > 0) return false;
  event.preventDefault();
  event.stopPropagation();
  if (sketchWallTemplatePlacement) return placeSketchWallTemplate(event);
  const draw = S.ui.sketchWallDraw;
  const point = constrainDrawingPoint(sketchNodeById(draw.startNodeId), worldFromMouseEvent(event), event.shiftKey);

  if (!draw.startNodeId) {
    // A brand-new chain's first point is pinned in place by default: with
    // nothing else anchoring the sketch, editing any length/angle later
    // would be free to redistribute across every node (least-squares'
    // minimal-move solution), which reads as the whole shape drifting
    // instead of "only that one measurement changed". Snapping onto an
    // existing node instead never fixes it — the existing sketch's own
    // constraints already govern it. Pinning the point alone only stops
    // translation, though — the wall growing from it can still freely
    // rotate around that point, so its angle needs its own separate pin
    // too; see the `wall.fixed`/`fixedAngle` assignment below, right after
    // that first segment is actually created.
    let startNode = findNearestSketchNode(point, sketchWallSnapTolerance());
    if (!startNode) {
      // Not near a node, but maybe on an existing wall's body — start a
      // T-Verbindung by splitting that wall exactly at the clicked point
      // (see splitSketchWall) and drawing on from the new joint node.
      const wallHit = findNearestSketchWallPoint(point, sketchWallSnapTolerance());
      if (wallHit) {
        recordUndo('Wand teilen');
        const split = splitSketchWall(wallHit.wall.id, wallHit.point);
        if (split) {
          solveSketchWalls();
          startNode = split.midNode;
        }
      }
    }
    if (!startNode) startNode = createSketchNode(point.x, point.z, true);
    draw.startNodeId = startNode.id;
    draw.currentPoint = { x: startNode.x, z: startNode.z };
    setStatus('Wand zeichnen · nächsten Punkt setzen (Umschalt: 45°-Schritte)');
    requestSketchWallPlanRender('sketch-wall-draw-start');
    return true;
  }

  const startNode = sketchNodeById(draw.startNodeId);
  if (!startNode) {
    draw.startNodeId = null;
    return true;
  }
  let endNode = findNearestSketchNode(point, sketchWallSnapTolerance());
  if (endNode && (endNode.id === startNode.id || (event.shiftKey && !pointOnDrawingRay(startNode, endNode, point)))) endNode = null;
  if (!endNode) {
    const wallHit = findNearestSketchWallPoint(point, sketchWallSnapTolerance());
    if (wallHit && (!event.shiftKey || pointOnDrawingRay(startNode, wallHit.point, point)) && wallHit.wall.startNodeId !== startNode.id && wallHit.wall.endNodeId !== startNode.id) {
      recordUndo('Wand teilen');
      const split = splitSketchWall(wallHit.wall.id, wallHit.point);
      if (split) {
        solveSketchWalls();
        endNode = split.midNode;
      }
    }
  }
  const length = endNode
    ? Math.hypot(endNode.x - startNode.x, endNode.z - startNode.z)
    : Math.hypot(point.x - startNode.x, point.z - startNode.z);
  if (length < SKETCH_WALL_MIN_LENGTH) {
    setStatus('Segment zu kurz');
    requestSketchWallPlanRender('sketch-wall-draw-too-short');
    return true;
  }

  // The chain's very first segment, growing from a freshly-pinned start
  // point (see the comment above): fix the whole wall, not just that one
  // point, exactly like Horizontal/Vertikal does for an explicit alignment
  // (see alignSelectedSketchWall) — otherwise the point stays put but the
  // wall itself is still free to pivot around it whenever a later edit
  // elsewhere in the group gives the solver room to do so. A node snapped
  // onto an already-existing (and possibly already-fixed) node must NOT
  // retrigger this — only a truly brand-new, still wall-less start point
  // counts as "the first wall of a new chain".
  const isFirstWallOfNewChain = startNode.fixed && sketchWallsAtNode(startNode.id).length === 0;

  recordUndo('Wand zeichnen');
  if (!endNode) endNode = createSketchNode(point.x, point.z, false);
  const wall = createSketchWall(startNode.id, endNode.id, sketchWallDrawDefaults());
  applySketchTJunctionStemAllowanceAtNode(startNode.id);
  applySketchTJunctionStemAllowanceAtNode(endNode.id);
  if (isFirstWallOfNewChain) {
    wall.fixed = true;
    wall.fixedSource = 'chain';
    wall.fixedAngle = sketchExactAngleRadians(sketchWallAbsoluteAngle(wall));
  }
  solveSketchWalls();
  draw.startNodeId = endNode.id;
  draw.currentPoint = { x: endNode.x, z: endNode.z };
  setStatus('Wand erstellt · nächsten Punkt setzen (Esc zum Beenden)');
  requestSketchWallAppRefresh('sketch-wall-draw-commit');
  return true;
}

function updateGlobalSketchWallDrawPreview(event) {
  if (!sketchWallDrawToolActive()) return false;
  if (sketchWallTemplatePlacement) {
    if (getSketchWallTemplatePreview()) {
      sketchWallTemplatePlacement.point = worldFromMouseEvent(event);
      requestSketchWallPlanRender('sketch-wall-template-preview');
      setSketchWallDrawCursor(null);
    }
    return true;
  }
  const draw = S.ui.sketchWallDraw;
  const point = constrainDrawingPoint(sketchNodeById(draw.startNodeId), worldFromMouseEvent(event), event.shiftKey);
  if (draw.startNodeId) {
    draw.currentPoint = point;
    requestSketchWallPlanRender('sketch-wall-draw-preview');
  }
  const kind = sketchWallHoverConnectionKind(point, sketchWallSnapTolerance(), draw.startNodeId);
  setSketchWallDrawCursor(kind);
  return true;
}

let boundSketchWallSvg = null;

function ensureSketchWallGlobalListeners() {
  const svg = getPlanSvg();
  if (!svg || svg === boundSketchWallSvg) return;
  boundSketchWallSvg = svg;

  // Capture phase so an active draw/opening/split tool owns every plan
  // click before it can reach any other element's own (bubble-phase) click
  // handler. Mutually exclusive via the shared S.ui.sketchWallTool state,
  // so at most one of these actually does anything on a given click.
  svg.addEventListener('click', event => {
    handleGlobalSketchWallDrawClick(event);
  }, true);
  svg.addEventListener('click', event => {
    handleGlobalSketchWallOpeningClick(event);
  }, true);
  svg.addEventListener('click', event => {
    handleGlobalSketchWallSplitClick(event);
  }, true);
  svg.addEventListener('click', event => {
    handleGlobalSketchWallParallelClick(event);
  }, true);

  svg.addEventListener('pointermove', event => {
    updateGlobalSketchWallDrawPreview(event);
  });

  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && sketchWallDrawToolActive()) {
      deactivateSketchWallDrawTool();
      return;
    }
    if (event.key === 'Escape' && sketchWallOpeningToolActive()) {
      deactivateSketchWallOpeningTool();
      return;
    }
    if (event.key === 'Escape' && sketchWallSplitToolActive()) {
      deactivateSketchWallSplitTool();
      return;
    }
    if (event.key === 'Escape' && sketchWallParallelToolActive()) {
      deactivateSketchWallParallelTool();
      return;
    }
    if ((event.key === 'Delete' || event.key === 'Backspace') && !isTypingTarget(event.target)) {
      const sel = S.selection?.sketchWall;
      if ((S.selection?.sketchWallIds || []).length > 1) return;
      if (sel?.wallId && sel?.openingId) {
        // A selected Ausschnitt takes priority over the wall it lives on —
        // otherwise Delete would always remove the whole wall, and there'd
        // be no keyboard way to remove just the marked opening.
        recordUndo('Ausschnitt löschen');
        deleteSketchWallOpening(sel.wallId, sel.openingId);
        selectSketchWallEntityExclusive({ wallId: sel.wallId });
        requestSketchWallAppRefresh('sketch-wall-opening-delete');
      } else if (sel?.wallId) {
        recordUndo('Wand löschen');
        deleteSketchWall(sel.wallId);
        solveSketchWalls();
        clearSketchWallSelection();
        requestSketchWallAppRefresh('sketch-wall-delete');
      }
    }
  });
}

let sketchNodeDrag = null;

function bindSketchNodeInteraction(el, node) {
  el.addEventListener('pointerdown', event => {
    if (event.button !== 0 || activeTool()?.owner && activeTool().owner !== 'core') return;
    // While actively drawing, the click-chain handler owns every click
    // (including snapping onto or closing back onto this very node) — let
    // it through undisturbed instead of also selecting/dragging the node.
    if (sketchWallDrawToolActive()) return;
    event.preventDefault();
    event.stopPropagation();
    selectSketchWallEntityExclusive({ nodeId: node.id });
    if (node.fixed || node.free === false) {
      setStatus('Punkt ist vollständig definiert und kann nicht verschoben werden');
      requestSketchWallAppRefresh('sketch-wall-node-select');
      return;
    }
    recordUndo('Punkt verschieben');
    sketchNodeDrag = { nodeId: node.id, pointerId: event.pointerId };
    el.setPointerCapture?.(event.pointerId);
    // Dragging a node is always "extending" some wall — defaults to the
    // Eckverbindung cursor ('L') even before the first move, since that's
    // what it becomes in most cases; a genuine wall-body hit upgrades it
    // to 'T' once the pointer actually moves.
    setSketchWallDrawCursor('node');
    requestSketchWallAppRefresh('sketch-wall-node-select');
  });
}

function initSketchNodeDragListeners() {
  document.addEventListener('pointermove', event => {
    if (!sketchNodeDrag || sketchNodeDrag.pointerId !== event.pointerId) return;
    const node = sketchNodeById(sketchNodeDrag.nodeId);
    if (!node) {
      sketchNodeDrag = null;
      return;
    }
    const point = worldFromMouseEvent(event);
    node.x = point.x;
    node.z = point.z;
    solveSketchWalls();
    // Free space still defaults to 'L' (see bindSketchNodeInteraction) —
    // only an actual node/wall-body hit changes what's shown.
    const kind = sketchWallHoverConnectionKind(point, sketchWallSnapTolerance(), node.id) || 'node';
    setSketchWallDrawCursor(kind);
    requestSketchWallPlanRender('sketch-wall-node-drag');
  });
  document.addEventListener('pointerup', event => {
    if (!sketchNodeDrag || sketchNodeDrag.pointerId !== event.pointerId) return;
    const { nodeId } = sketchNodeDrag;
    sketchNodeDrag = null;
    clearSketchWallDrawCursor();
    attemptSketchWallSnapMerge(new Set([nodeId]));
    solveSketchWalls();
    requestSketchWallAppRefresh('sketch-wall-node-drop');
  });
}

let sketchOpeningDrag = null;

// Moving an Ausschnitt only ever changes its `offset` along its own wall's
// axis — it's never reassigned to a different wall, so it can't cross onto
// a neighboring segment even where two walls are collinear and touching.
function bindSketchWallOpeningInteraction(el, wall, opening) {
  el.addEventListener('pointerdown', event => {
    if (event.button !== 0 || activeTool()?.owner && activeTool().owner !== 'core') return;
    if (sketchWallDrawToolActive() || sketchWallOpeningToolActive()) return;
    event.preventDefault();
    event.stopPropagation();
    selectSketchWallEntityExclusive({ wallId: wall.id, openingId: opening.id });
    recordUndo('Ausschnitt verschieben');
    sketchOpeningDrag = { wallId: wall.id, openingId: opening.id, pointerId: event.pointerId };
    el.setPointerCapture?.(event.pointerId);
    requestSketchWallAppRefresh('sketch-wall-opening-drag-start');
  });
  // The browser still synthesizes a 'click' after pointerdown+pointerup on
  // the same element even though selection is fully handled above — left
  // alone, it bubbles to the SVG's "deselect on background click" listener
  // and immediately undoes the selection this element just made (same
  // issue bindSketchWallInteraction works around for the wall body).
  el.addEventListener('click', event => event.stopPropagation());
  el.addEventListener('dblclick', event => {
    if (event.button !== 0 || activeTool() || S.ui?.activeTool || sketchWallDrawToolActive()) return;
    event.preventDefault();
    event.stopPropagation();
    selectConnectedSketchWalls(wall.id);
  });
}

function initSketchWallOpeningDragListeners() {
  document.addEventListener('pointermove', event => {
    if (!sketchOpeningDrag || sketchOpeningDrag.pointerId !== event.pointerId) return;
    const wall = sketchWallById(sketchOpeningDrag.wallId);
    if (!wall) {
      sketchOpeningDrag = null;
      return;
    }
    const opening = (wall.openings || []).find(entry => entry.id === sketchOpeningDrag.openingId);
    if (!opening) {
      sketchOpeningDrag = null;
      return;
    }
    const point = worldFromMouseEvent(event);
    const start = sketchNodeById(wall.startNodeId);
    const end = sketchNodeById(wall.endNodeId);
    if (!start || !end) return;
    const length = Math.hypot(end.x - start.x, end.z - start.z) || 1;
    const dirX = (end.x - start.x) / length;
    const dirZ = (end.z - start.z) / length;
    const along = (point.x - start.x) * dirX + (point.z - start.z) * dirZ;
    updateSketchWallOpening(wall.id, opening.id, { offset: along });
    requestSketchWallPlanRender('sketch-wall-opening-drag');
  });
  document.addEventListener('pointerup', event => {
    if (!sketchOpeningDrag || sketchOpeningDrag.pointerId !== event.pointerId) return;
    sketchOpeningDrag = null;
    requestSketchWallAppRefresh('sketch-wall-opening-drop');
  });
}

const SKETCH_WALL_DRAG_THRESHOLD_PX = 4;
let sketchGroupDrag = null;

function initSketchGroupDragListeners() {
  document.addEventListener('pointermove', event => {
    if (!sketchGroupDrag || sketchGroupDrag.pointerId !== event.pointerId) return;
    if (!sketchGroupDrag.moved) {
      const movedPx = Math.hypot(event.clientX - sketchGroupDrag.startClientX, event.clientY - sketchGroupDrag.startClientY);
      if (movedPx < SKETCH_WALL_DRAG_THRESHOLD_PX) return;
      sketchGroupDrag.moved = true;
      recordUndo('Wand verschieben');
    }
    const current = worldFromMouseEvent(event);
    const dx = current.x - sketchGroupDrag.startWorld.x;
    const dz = current.z - sketchGroupDrag.startWorld.z;
    for (const snapshot of sketchGroupDrag.nodeSnapshot) {
      const node = sketchNodeById(snapshot.id);
      if (node) {
        node.x = snapshot.x + dx;
        node.z = snapshot.z + dz;
      }
    }
    requestSketchWallPlanRender('sketch-wall-group-drag');
  });
  document.addEventListener('pointerup', event => {
    if (!sketchGroupDrag || sketchGroupDrag.pointerId !== event.pointerId) return;
    const { moved, wall, nodeSnapshot, furring } = sketchGroupDrag;
    sketchGroupDrag = null;
    if (moved) {
      attemptSketchWallSnapMerge(new Set(nodeSnapshot.map(snapshot => snapshot.id)));
      solveSketchWalls();
      requestSketchWallAppRefresh('sketch-wall-group-drag-drop');
      return;
    }
    // No real movement — treat it as the plain click it visually was. The
    // Vormauerung has no separate id/position of its own (it's a property
    // of the wall), so this still selects the same underlying wall — only
    // `furring: true` differs, which is what drives which sidebar card
    // (Parametrische Wände vs. Vormauerung) comes forward.
    selectSketchWallEntityExclusive({ wallId: wall.id, furring });
    requestSketchWallAppRefresh('sketch-wall-select');
  });
}

// Grabbing a wall body moves the wall's own connected component (every
// node reachable from it via shared walls) as one rigid group — fixed
// nodes included, so lengths/angles within that group never change. A
// separate, unconnected wall chain is not part of this group and stays
// exactly where it is, so two independent chains can be positioned
// relative to each other. This is a different kind of freedom than the
// constraint solver: "fixed" pins a point relative to the rest of *its
// own* group, not to the world, so the group can still be repositioned.
// A plain click (no real movement) falls through to the normal select
// behavior instead of starting a drag.
function bindSketchWallInteraction(el, wall, options = {}) {
  const furring = options.furring === true;
  el.addEventListener('pointerdown', event => {
    if (event.button !== 0 || activeTool()?.owner && activeTool().owner !== 'core') return;
    // Draw and Parallel both need to treat a click on a wall's body as
    // "pick this wall for the tool", not "select/drag it normally" — a
    // capture-phase 'click' handler further up (see
    // handleGlobalSketchWallDrawClick / handleGlobalSketchWallParallelClick)
    // already owns that click, but 'pointerdown' fires earlier, as its own
    // separate event, and isn't affected by that handler's
    // stopPropagation() on 'click' — so it needs its own explicit bail-out
    // here too, or clicking a DIFFERENT wall as Parallel's reference would
    // silently reselect that wall instead of leaving the original selection
    // (the wall actually being rotated) alone.
    if (sketchWallDrawToolActive() || sketchWallParallelToolActive()) return;
    const selectedWallIds = S.selection?.sketchWallIds || [];
    if (event.ctrlKey || event.metaKey || (selectedWallIds.length > 1 && selectedWallIds.includes(wall.id))) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const connectedNodeIds = sketchWallConnectedNodeIds(wall);
    sketchGroupDrag = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startWorld: worldFromMouseEvent(event),
      nodeSnapshot: allSketchNodes()
        .filter(node => connectedNodeIds.has(node.id))
        .map(node => ({ id: node.id, x: node.x, z: node.z })),
      moved: false,
      wall,
      furring
    };
    el.setPointerCapture?.(event.pointerId);
  });
  // The browser still synthesizes a 'click' after pointerdown+pointerup on
  // the same element even though selection/dragging is fully handled above
  // — left alone, it bubbles to the SVG's "deselect on background click"
  // listener and immediately undoes the selection this element just made.
  el.addEventListener('click', event => event.stopPropagation());
  el.addEventListener('dblclick', event => {
    if (event.button !== 0 || activeTool() || S.ui?.activeTool || sketchWallDrawToolActive()) return;
    event.preventDefault();
    event.stopPropagation();
    selectConnectedSketchWalls(wall.id);
  });
}

let activeSketchWallValueEditor = null;

function closeSketchWallValueEditor() {
  if (activeSketchWallValueEditor?.isConnected) activeSketchWallValueEditor.remove();
  activeSketchWallValueEditor = null;
}

// Small floating input positioned at the clicked dimension label — avoids a
// native window.prompt() so it can sit inline over the plan like a real
// editable dimension chip.
function promptSketchWallValue(event, currentValue, onCommit) {
  closeSketchWallValueEditor();
  const input = document.createElement('input');
  input.type = 'text';
  input.inputMode = 'decimal';
  input.className = 'sketch-wall-value-editor';
  input.value = String(Number(currentValue.toFixed(10)));
  input.style.left = `${event.clientX}px`;
  input.style.top = `${event.clientY}px`;
  document.body.appendChild(input);
  activeSketchWallValueEditor = input;
  input.focus();
  input.select();

  // Removing the focused input can synchronously re-fire 'blur' before this
  // function even returns, so guard re-entrancy with a local flag rather
  // than the (not-yet-cleared) shared editor reference.
  let settled = false;
  const commit = () => {
    if (settled) return;
    settled = true;
    const raw = input.value.trim();
    closeSketchWallValueEditor();
    if (raw === '') {
      // Clicking a dimension and confirming an empty field is the explicit
      // "make this one undefined again" gesture — deliberate and always
      // targets the dimension the user actually clicked, rather than the
      // solver silently picking some other constraint to relax.
      onCommit(null);
      return;
    }
    const value = Number(raw.replace(',', '.'));
    if (!Number.isFinite(value)) {
      setStatus('Ungültiger Wert');
      return;
    }
    onCommit(value);
  };
  const cancel = () => {
    if (settled) return;
    settled = true;
    closeSketchWallValueEditor();
  };
  input.addEventListener('keydown', keyEvent => {
    keyEvent.stopPropagation();
    if (keyEvent.key === 'Enter') commit();
    else if (keyEvent.key === 'Escape') cancel();
  });
  input.addEventListener('blur', commit);
  input.addEventListener('click', keyEvent => keyEvent.stopPropagation());
}

// A big jump straight to the new target (e.g. 1660mm -> 500mm across a
// sketch with a dozen other constraints) can throw the Gauss-Newton solve
// far enough from any valid configuration that it settles into a distorted
// near-miss instead of the true solution — the edited value converges while
// unrelated 90° angles elsewhere quietly stop holding. Stepping the target
// in small increments keeps every intermediate solve close to the previous
// (already-valid) one, which is what makes each step reliably converge.
function stepSolveTowardValue(setValue, fromValue, toValue, steps = 12, solveOptions = {}) {
  let result = { ok: true, converged: true };
  for (let i = 1; i <= steps; i += 1) {
    setValue(fromValue + (toValue - fromValue) * (i / steps));
    result = solveSketchWalls(solveOptions);
    if (!result.ok) return result;
  }
  return result;
}

function sketchEditBaseMovementWeights() {
  const weights = new Map(allSketchNodes().map(node => [node.id, 1]));
  const raise = (nodeId, weight) => weights.set(nodeId, Math.max(weights.get(nodeId) || 1, weight));
  for (const wall of allSketchWalls()) {
    const explicitLength = Number.isFinite(wall.length) && Boolean(wall.lengthMode);
    const weight = wall.fixed ? 10000 : explicitLength ? 100 : 1;
    raise(wall.startNodeId, weight);
    raise(wall.endNodeId, weight);
  }
  return weights;
}

function sketchExplicitlyFixedNodeIds() {
  const nodeIds = new Set();
  for (const wall of allSketchWalls()) {
    if (!wall.fixed || wall.fixedSource !== 'user') continue;
    nodeIds.add(wall.startNodeId);
    nodeIds.add(wall.endNodeId);
  }
  return nodeIds;
}

function sketchBranchNodeIds(wall, pivotNodeId) {
  const firstNodeId = sketchWallOtherNodeId(wall, pivotNodeId);
  if (!firstNodeId) return new Set();
  const visited = new Set([firstNodeId]);
  const queue = [firstNodeId];
  while (queue.length) {
    const nodeId = queue.shift();
    for (const branchWall of sketchWallsAtNode(nodeId)) {
      const otherId = sketchWallOtherNodeId(branchWall, nodeId);
      if (!otherId || otherId === pivotNodeId || visited.has(otherId)) continue;
      visited.add(otherId);
      queue.push(otherId);
    }
  }
  return visited;
}

function raiseSketchBranchWeight(weights, wall, pivotNodeId, factor) {
  for (const nodeId of sketchBranchNodeIds(wall, pivotNodeId)) {
    weights.set(nodeId, (weights.get(nodeId) || 1) * factor);
  }
}

function sketchAngleEditSolveOptions(angle, driver) {
  if (driver) return {};
  const weights = sketchEditBaseMovementWeights();
  const wallA = sketchWallById(angle.wallAId);
  const joint = allSketchStraightJoints().find(entry => entry.nodeId === angle.nodeId) || null;
  let anchorNodeId = angle.nodeId;

  if (
    !joint
    && wallA
    && Number.isFinite(wallA.length)
    && (wallA.lengthMode === 'inner' || wallA.lengthMode === 'outer')
  ) {
    const farNodeId = sketchWallOtherNodeId(wallA, angle.nodeId);
    if (
      farNodeId
      && sketchLengthEndpointScore(farNodeId, wallA.id) > sketchLengthEndpointScore(angle.nodeId, wallA.id)
    ) {
      // An inner/outer dimension measures the mitered face, not the wall's
      // centreline. Changing this corner angle therefore changes that face
      // length even when both centreline endpoints stay put. If wallA's far
      // side is already the stronger reference, keep that side fixed and let
      // the edited corner slide along wallA to preserve the entered length.
      anchorNodeId = farNodeId;
    }
  }

  const lockedNodeIds = new Set([...sketchExplicitlyFixedNodeIds(), anchorNodeId]);

  if (joint) {
    // At a T-junction the straight-through run is the stable reference and
    // the stem is the part that should swing around the junction.
    for (const throughWallId of [joint.wallAId, joint.wallBId]) {
      const throughWall = sketchWallById(throughWallId);
      if (throughWall) raiseSketchBranchWeight(weights, throughWall, angle.nodeId, 25);
    }
    const throughWalls = [joint.wallAId, joint.wallBId]
      .map(sketchWallById)
      .filter(Boolean);
    const throughRunIsUnmeasured = throughWalls.length === 2 && throughWalls.every(throughWall => (
      !Number.isFinite(throughWall.length) || !throughWall.lengthMode
    ));
    if (throughRunIsUnmeasured) {
      // A completely red through-run has no dimension that needs geometric
      // compensation. Keep its two outer endpoints exactly where they were
      // and swing only the T-stem, as a drafter expects from editing the
      // junction angle. Black partial dimensions intentionally stay movable:
      // their endpoints must grow outwards when the diagonal stem projection
      // gets wider.
      for (const throughWall of throughWalls) {
        const outerNodeId = sketchWallOtherNodeId(throughWall, angle.nodeId);
        if (outerNodeId) lockedNodeIds.add(outerNodeId);
      }
    }
  } else if (wallA) {
    // For a regular chain, wallA is the already-existing/reference side and
    // wallB is the subsequently attached side. Explicit black dimensions on
    // wallB still outweigh this modest reference preference (100 > 25).
    raiseSketchBranchWeight(weights, wallA, angle.nodeId, 25);
  }

  const connected = wallA && sketchWallConnectedNodeIds(wallA);
  const chainWalls = connected && allSketchWalls().filter(wall => connected.has(wall.startNodeId));
  const simpleChain = connected && [...connected].every(id => sketchWallsAtNode(id).length <= 2)
    && chainWalls.length === connected.size - 1;
  if (simpleChain) {
    const reference = sketchEditReferenceWall(wallA);
    const refNodes = [reference.startNodeId, reference.endNodeId];
    const pivotId = refNodes.find(id => sketchNodeById(id)?.fixed)
      || (refNodes.includes(angle.nodeId) ? sketchWallOtherNodeId(reference, angle.nodeId) : reference.startNodeId);
    const referenceLocks = sketchExplicitlyFixedNodeIds();
    // A measured reference may need miter compensation along its own axis.
    if (reference.lengthMode && Number.isFinite(reference.length)) {
      refNodes.forEach(id => referenceLocks.delete(id));
    }
    referenceLocks.add(pivotId);
    // A remote angle does not change either miter of the reference wall.
    // Hold both endpoints exactly instead of accumulating solver drift.
    if (!refNodes.includes(angle.nodeId)) refNodes.forEach(id => referenceLocks.add(id));
    const additionalConstraints = [];
    if (!reference.horizontal && !reference.vertical && !(reference.fixed && Number.isFinite(reference.fixedAngle))) {
      additionalConstraints.push({ type: 'fixedAngle', ref: reference,
        target: sketchWallAbsoluteAngle(reference), tolerance: 1e-8 });
    }
    // Unauthored chain dimensions are preserved for this edit without turning
    // them into permanent user dimensions. T-junctions keep their own policy.
    for (const entry of chainWalls) {
      if (entry.lengthMode || entry === reference && entry.fixedSource === 'user') continue;
      additionalConstraints.push({ type: 'innerLength', ref: entry,
        target: sketchWallSideLength(entry, sketchWallRenderPoint, 1), tolerance: 0.01 });
    }
    for (const entry of allSketchAngles()) {
      if (entry.id === angle.id || !connected.has(entry.nodeId) || Number.isFinite(entry.value)) continue;
      additionalConstraints.push({ type: 'angle', ref: entry,
        target: sketchDirectedAngleValueAt(entry.nodeId, entry.wallAId, entry.wallBId), tolerance: 1e-8 });
    }
    return { movementWeights: weights, lockedNodeIds: referenceLocks, additionalConstraints };
  }

  return {
    movementWeights: weights,
    // Normally the corner itself is the rotation pivot. A dimensioned wall
    // with a more strongly anchored far side is the geometric exception
    // above: its corner has to slide so its mitered face length can stay put.
    lockedNodeIds
  };
}

function sketchLengthEndpointScore(nodeId, editedWallId) {
  const visitedNodes = new Set([nodeId]);
  const visitedWalls = new Set();
  const queue = [nodeId];
  let score = sketchNodeById(nodeId)?.fixed ? 1000000 : 0;
  while (queue.length) {
    const currentId = queue.shift();
    for (const wall of sketchWallsAtNode(currentId)) {
      if (wall.id === editedWallId || visitedWalls.has(wall.id)) continue;
      visitedWalls.add(wall.id);
      if (wall.fixed) score += 10000;
      else if (Number.isFinite(wall.length) && wall.lengthMode) score += 100;
      const otherId = sketchWallOtherNodeId(wall, currentId);
      if (!otherId || visitedNodes.has(otherId)) continue;
      visitedNodes.add(otherId);
      if (sketchNodeById(otherId)?.fixed) score += 1000000;
      queue.push(otherId);
    }
  }
  return score;
}

// A length edit translates one complete branch along the existing wall axis.
// Least-squares movement is unsuitable here: it can rotate an otherwise free U.
function sketchEditReferenceWall(wall) {
  const ids = sketchWallConnectedNodeIds(wall);
  const walls = allSketchWalls().filter(entry => ids.has(entry.startNodeId));
  return walls.find(entry => entry.fixed && entry.fixedSource === 'user')
    || walls.find(entry => entry.fixed && entry.fixedSource === 'chain')
    || walls[0];
}

function sketchNodesWithoutWall(startNodeId, excludedWallId) {
  const ids = new Set([startNodeId]), queue = [startNodeId];
  while (queue.length) {
    for (const entry of sketchWallsAtNode(queue.shift())) {
      if (entry.id === excludedWallId) continue;
      for (const id of [entry.startNodeId, entry.endNodeId]) {
        if (!ids.has(id)) { ids.add(id); queue.push(id); }
      }
    }
  }
  return ids;
}

function sketchWallRenderPoint(nodeId) {
  const node = sketchNodeById(nodeId);
  return node ? { x: node.x, z: node.z } : { x: 0, z: 0 };
}

function selectedSketchWall() {
  const wallId = S.selection?.sketchWall?.wallId;
  const wall = wallId ? sketchWallById(wallId) : null;
  if (!wall) setStatus('Bitte zuerst eine Wand auswählen');
  return wall;
}

// "Schnitt": arms the split tool for the selected wall (see
// activateSketchWallSplitTool) instead of splitting immediately at its
// midpoint — the actual cut happens wherever the user then clicks on that
// wall's own body (handleGlobalSketchWallSplitClick), so it can land
// exactly between two Ausschnitte instead of always in the middle. Clicking
// the button again while already armed for this same wall cancels it (see
// toggleSketchWallSplitTool) — same 180°-lock a mid-wall T-Verbindung sets
// up automatically once the cut lands, just triggered from the sidebar
// instead of by drawing a stem wall onto this one.
function splitSelectedSketchWall() {
  const wall = selectedSketchWall();
  if (!wall) return;
  toggleSketchWallSplitTool(wall.id);
}

// "Parallel": arms the parallel tool for the selected wall (see
// activateSketchWallParallelTool) instead of asking upfront which wall to
// match — the reference wall is whichever one the user then clicks (see
// handleGlobalSketchWallParallelClick). Clicking the button again while
// already armed for this same wall cancels it, same as Schnitt.
function parallelSelectedSketchWall() {
  const wall = selectedSketchWall();
  if (!wall) return;
  toggleSketchWallParallelTool(wall.id);
}

// The Fixiert button acts on the currently-selected wall (not a "arm tool,
// then click" flow) — pins/unpins that wall's absolute angle, which is what
// anchors the whole sketch's rotation (see toggleSketchWallFixed).
function toggleSelectedSketchWallFixed() {
  const wall = selectedSketchWall();
  if (!wall) return;
  recordUndo('Wand fixieren');
  toggleSketchWallFixed(wall.id);
  solveSketchWalls();
  requestSketchWallAppRefresh('sketch-wall-fixed-toggle');
}

// A relative angle normally rotates whichever adjacent wall still has a free
// direction.  A newly drawn chain, however, deliberately pins its first wall
// so the whole sketch cannot drift; users can additionally align the next
// wall horizontally/vertically.  At that point the relative angle is
// redundant with two absolute directions.  Editing the angle must move the
// plain chain anchor along with it, otherwise the old anchor and the new angle
// contradict each other and the edit is immediately rolled back.
//
// Axis-aligned walls remain authoritative.  If both sides are explicitly
// horizontal/vertical, there is no safe implicit override and the regular
// contradiction handling below still applies.
function sketchAngleAdjustableFixedDriver(angle) {
  const wallA = sketchWallById(angle?.wallAId);
  const wallB = sketchWallById(angle?.wallBId);
  if (!wallA || !wallB) return null;
  const hasDirectionConstraint = wall => Boolean(
    wall.horizontal
    || wall.vertical
    || (wall.fixed && Number.isFinite(wall.fixedAngle))
  );
  if (!hasDirectionConstraint(wallA) || !hasDirectionConstraint(wallB)) return null;

  const isPlainFixed = wall => Boolean(
    wall.fixed
    && Number.isFinite(wall.fixedAngle)
    && wall.fixedSource === 'chain'
    && !wall.horizontal
    && !wall.vertical
  );
  if (isPlainFixed(wallA)) return { wall: wallA, degreeFactor: -1 };
  if (isPlainFixed(wallB)) return { wall: wallB, degreeFactor: 1 };
  return null;
}

function solveSketchAngleEdit(angle, targetDegrees) {
  if (!angle || !(targetDegrees > 0 && targetDegrees < 360)) return { ok: false, converged: false };
  const currentDirected = sketchDirectedAngleValueAt(angle.nodeId, angle.wallAId, angle.wallBId);
  const fromDegrees = currentDirected * (180 / Math.PI);
  const toDegrees = targetDegrees;
  const originalAngleValue = angle.value;
  const originalNodePositions = allSketchNodes().map(node => ({ node, x: node.x, z: node.z }));
  const siblingId = sketchAnglePairedSiblingId(angle.id);
  const sibling = siblingId ? allSketchAngles().find(entry => entry.id === siblingId) : null;
  const originalSiblingValue = sibling?.value;
  const driver = sketchAngleAdjustableFixedDriver(angle);
  const originalFixedAngle = driver?.wall.fixedAngle;

  const setAngle = valueDegrees => {
    updateSketchAngleValueDegrees(angle.id, valueDegrees);
    if (siblingId) updateSketchAngleValueDegrees(siblingId, null);
    if (driver) {
      const deltaRadians = driver.degreeFactor * (valueDegrees - fromDegrees) * (Math.PI / 180);
      driver.wall.fixedAngle = sketchExactAngleRadians(originalFixedAngle + deltaRadians);
    }
  };
  // Acute angles are numerically much more sensitive than broad ones. A
  // fixed twelve-step split turns 90° -> 9° into a final 16° -> 9° jump,
  // which can strand the nonlinear solver near 10°. Steps of at most two
  // degrees are robust while retaining the exact fractional target.
  const steps = Math.max(12, Math.ceil(Math.abs(toDegrees - fromDegrees) / 2));
  const result = stepSolveTowardValue(
    setAngle,
    fromDegrees,
    toDegrees,
    steps,
    sketchAngleEditSolveOptions(angle, driver)
  );
  if (result.ok) return result;

  // Restore the exact pre-edit constraint state, including a free angle or a
  // paired T-junction angle.  Replacing it with the displayed rounded value
  // would create a new constraint even though the attempted edit failed.
  angle.value = originalAngleValue;
  if (sibling) sibling.value = originalSiblingValue;
  if (driver) driver.wall.fixedAngle = originalFixedAngle;
  for (const original of originalNodePositions) {
    original.node.x = original.x;
    original.node.z = original.z;
  }
  solveSketchWalls();
  return result;
}

// Rotates the selected wall to the nearest horizontal/vertical orientation
// and locks it there PERMANENTLY (wall.fixed stays true afterwards) — not
// just for the duration of the rotation itself. The rotation is driven
// through the solver via a 'fixedAngle' constraint (stepped like any other
// big value edit, see stepSolveTowardValue) rather than a manual rigid-body
// transform: every already-determined length/angle constraint elsewhere
// stays active and in the constraint list the whole time, so the solve
// *can't* violate them while satisfying the new angle target —
// free/underdetermined parts simply have nothing pinning them and settle
// wherever the solve leaves them.
//
// Staying fixed afterwards matters beyond just "does this wall visually
// look aligned right now": without it, this wall's own absolute angle has
// nothing pinning it going forward, so editing a completely different
// wall's length elsewhere in the same connected group can freely rotate
// this one again too via the solver's least-squares "smallest overall move"
// preference — an unpinned angle also gives the solver extra rotational
// freedom that can make *other* edits (e.g. an angle a few degrees away)
// converge inconsistently depending on the exact starting value, since
// there's a whole family of equally-valid rigid rotations to choose from
// instead of a single well-defined one.
function alignSelectedSketchWall(axis) {
  const wall = selectedSketchWall();
  if (!wall) return;

  // Once a wall is fully determined, its angle is already pinned by other
  // constraints elsewhere in the group — Horizontal/Vertikal here is purely
  // function 2 (one-time rotation of the whole rigid group), so it must not
  // also toggle the persistent axis flag (function 1) or the button would
  // stay lit for a constraint that isn't actually doing anything.
  const isFullyDetermined = wall.free === false;

  if (!isFullyDetermined && wall[axis]) {
    recordUndo(axis === 'horizontal' ? 'Wand horizontal aufheben' : 'Wand vertikal aufheben');
    setSketchWallAxis(wall.id, axis);
    solveSketchWalls();
    requestSketchWallAppRefresh('sketch-wall-axis-toggle');
    return;
  }

  const current = sketchWallAbsoluteAngle(wall);
  const candidates = axis === 'horizontal' ? [0, Math.PI] : [Math.PI / 2, -Math.PI / 2];
  let target = candidates[0];
  let bestDelta = Infinity;
  for (const candidate of candidates) {
    const delta = Math.abs(wrapAngle(candidate - current));
    if (delta < bestDelta) {
      bestDelta = delta;
      target = current + wrapAngle(candidate - current);
    }
  }

  recordUndo(axis === 'horizontal' ? 'Wand horizontal ausrichten' : 'Wand vertikal ausrichten');
  const wasFixed = wall.fixed;
  const previousFixedSource = wall.fixedSource;
  if (!wasFixed) wall.fixed = true;
  // A wall explicitly pinned by the user remains an absolute reference even
  // when it is subsequently aligned to an axis. The axis flag refines its
  // direction; it must not downgrade the stronger "Fixiert" meaning.
  wall.fixedSource = previousFixedSource === 'user' ? 'user' : 'axis';
  const setAngle = v => { wall.fixedAngle = v; };
  const result = stepSolveTowardValue(setAngle, current, target);
  if (!result.ok) {
    // Failed partway through — roll back to whatever it was before this
    // attempt instead of leaving it half-pinned at some intermediate angle.
    if (!wasFixed) {
      wall.fixed = false;
      wall.fixedAngle = null;
    }
    wall.fixedSource = previousFixedSource || null;
    solveSketchWalls();
    setStatus('Widerspruch: diese Wand kann nicht ausgerichtet werden — zuerst eine widersprüchliche Bemaßung aufheben');
    requestSketchWallAppRefresh('sketch-wall-align-conflict');
    return;
  }
  // Success: stay fixed at exactly this angle (wall.fixed/fixedAngle are
  // already set to `target` by the last step above) — see the function
  // comment for why this must persist rather than reverting.
  if (!isFullyDetermined) {
    setSketchWallAxis(wall.id, axis);
  }
  solveSketchWalls();
  requestSketchWallAppRefresh('sketch-wall-align');
}

function solveSketchLengthEdit(wall, mode, targetLength) {
  if (!wall || !['inner', 'outer'].includes(mode) || !(targetLength >= SKETCH_WALL_MIN_LENGTH)) {
    return { ok: false, converged: false };
  }
  const side = mode === 'inner' ? 1 : -1;
  const originalLength = wall.length;
  const originalMode = wall.lengthMode;
  const current = wall.lengthMode === mode && Number.isFinite(wall.length)
    ? wall.length
    : sketchWallSideLength(wall, sketchWallRenderPoint, side);
  const originalNodePositions = allSketchNodes().map(node => ({ node, x: node.x, z: node.z }));
  const reference = sketchEditReferenceWall(wall);
  const startBranch = sketchNodesWithoutWall(wall.startNodeId, wall.id);
  const endBranch = sketchNodesWithoutWall(wall.endNodeId, wall.id);
  const delta = targetLength - current;
  if (Math.abs(delta) > 1e-8 && startBranch.has(wall.endNodeId)) return { ok: false, converged: false };
  let keepStart;
  if (reference.id !== wall.id) keepStart = startBranch.has(reference.startNodeId);
  else keepStart = sketchLengthEndpointScore(wall.startNodeId, wall.id) >= sketchLengthEndpointScore(wall.endNodeId, wall.id);
  const movingIds = keepStart ? endBranch : startBranch;
  if (Math.abs(delta) > 1e-8 && ([...movingIds].some(id => sketchNodeById(id)?.fixed)
    || allSketchWalls().some(entry => entry.id !== wall.id && entry.fixed && entry.fixedSource === 'user'
      && movingIds.has(entry.startNodeId)))) return { ok: false, converged: false };
  const a = sketchNodeById(wall.startNodeId), b = sketchNodeById(wall.endNodeId);
  const centerLength = Math.hypot(b.x - a.x, b.z - a.z);
  if (centerLength + delta < SKETCH_WALL_MIN_LENGTH) return { ok: false, converged: false };
  const dx = (b.x - a.x) / centerLength * delta * (keepStart ? 1 : -1);
  const dz = (b.z - a.z) / centerLength * delta * (keepStart ? 1 : -1);
  for (const id of movingIds) { const node = sketchNodeById(id); node.x += dx; node.z += dz; }
  updateSketchWallLength(wall.id, targetLength, mode);
  const result = solveSketchWalls({ validateOnly: true });
  if (result.ok) return result;
  updateSketchWallLength(wall.id, originalLength, originalMode);
  for (const original of originalNodePositions) {
    original.node.x = original.x;
    original.node.z = original.z;
  }
  return result;
}

// Inner and outer length are mutually exclusive per wall — clicking either
// label edits `wall.length`/`wall.lengthMode` together, and setting one
// implicitly clears whichever the other used to be.
function bindSketchLengthLabelInteraction(el, wall, mode) {
  const side = mode === 'inner' ? 1 : -1;
  el.addEventListener('click', event => {
    if (activeTool()?.owner && activeTool().owner !== 'core') return;
    event.stopPropagation();
    const current = wall.lengthMode === mode && Number.isFinite(wall.length)
      ? wall.length
      : sketchWallSideLength(wall, sketchWallRenderPoint, side);
    promptSketchWallValue(event, current, value => {
      if (value === null) {
        recordUndo('Wandlänge aufheben');
        updateSketchWallLength(wall.id, null, null);
        solveSketchWalls();
        setStatus('Wandlänge wurde aufgehoben');
        requestSketchWallAppRefresh('sketch-wall-length-clear');
        return;
      }
      if (value < SKETCH_WALL_MIN_LENGTH) {
        setStatus('Länge zu klein');
        return;
      }
      recordUndo('Wandlänge ändern');
      const result = solveSketchLengthEdit(wall, mode, value);
      if (!result.ok) {
        setStatus('Widerspruch: diese Länge passt nicht zu den übrigen Bemaßungen — zuerst eine andere Bemaßung aufheben (anklicken, Feld leeren, Enter)');
      }
      requestSketchWallAppRefresh('sketch-wall-length-edit');
    });
  });
}

function bindSketchAngleLabelInteraction(el, angle) {
  el.addEventListener('click', event => {
    if (activeTool()?.owner && activeTool().owner !== 'core') return;
    event.stopPropagation();
    const currentDegrees = sketchAngleDisplayDegrees(angle);
    const reversed = sketchDirectedAngleValueAt(angle.nodeId, angle.wallAId, angle.wallBId) > Math.PI;
    promptSketchWallValue(event, currentDegrees, rawValue => {
      const siblingId = sketchAnglePairedSiblingId(angle.id);
      if (rawValue === null) {
        recordUndo('Winkel aufheben');
        updateSketchAngleValueDegrees(angle.id, null);
        if (siblingId) updateSketchAngleValueDegrees(siblingId, null);
        solveSketchWalls();
        setStatus('Winkel wurde aufgehoben');
        requestSketchWallAppRefresh('sketch-wall-angle-clear');
        return;
      }
      const value = rawValue;
      if (!(value > 0 && value < 360)) {
        setStatus('Winkel muss zwischen 0 und 360° liegen');
        return;
      }
      recordUndo('Winkel ändern');
      // The editor measures the displayed sector; keep the stored directed branch.
      const result = solveSketchAngleEdit(angle, reversed && value <= 180 ? 360 - value : value);
      if (!result.ok) {
        setStatus('Widerspruch: dieser Winkel passt nicht zu den übrigen Bemaßungen — zuerst eine andere Bemaßung aufheben (anklicken, Feld leeren, Enter)');
      }
      requestSketchWallAppRefresh('sketch-wall-angle-edit');
    });
  });
}

initSketchNodeDragListeners();
initSketchGroupDragListeners();
initSketchWallOpeningDragListeners();

export {
  getSketchWallTemplateSettings, updateSketchWallTemplateSettings, armSketchWallTemplate, getSketchWallTemplatePreview,
  sketchWallDrawToolActive,
  activateSketchWallDrawTool,
  deactivateSketchWallDrawTool,
  toggleSketchWallDrawTool,
  sketchWallOpeningToolActive,
  activateSketchWallOpeningTool,
  deactivateSketchWallOpeningTool,
  toggleSketchWallOpeningTool,
  sketchWallSplitToolActive,
  sketchWallParallelToolActive,
  toggleSelectedSketchWallFixed,
  alignSelectedSketchWall,
  splitSelectedSketchWall,
  parallelSelectedSketchWall,
  attemptSketchWallSnapMerge,
  handleGlobalSketchWallDrawClick,
  handleGlobalSketchWallParallelClick,
  ensureSketchWallGlobalListeners,
  bindSketchNodeInteraction,
  bindSketchWallInteraction,
  bindSketchWallOpeningInteraction,
  bindSketchLengthLabelInteraction,
  bindSketchAngleLabelInteraction,
  solveSketchLengthEdit,
  solveSketchAngleEdit
};
